import React, { useMemo } from 'react';
import {
  Button,
  Typography,
  useForm,
  useFieldArray,
  FieldValues,
  UseFieldArrayReturn,
  Controller,
  FieldError,
} from '@otp/ui';
import { Close } from '@otp/icons';
import { useTranslation } from 'react-i18next';
import { getComponentByType } from './fields-factory';
import clsx from 'clsx';
import { getFieldsConfig } from './fields-config';
import { LoginRuleCriteria } from 'src/components/login-rules/form/types';
import { useLoginRuleUserPoolFields } from 'src/hooks/query/login-rules/use-login-rule-user-pool-fields';

type Form = FieldValues & {
  names: Array<any>;
};

type MultipleFormField = Record<string, UseFieldArrayReturn<Form>>;

export interface LoginRuleCriteriaFormProps {
  defaultValues?: LoginRuleCriteria;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export const LoginRuleCriteriaForm = ({
  defaultValues = {},
  onSubmit: onSubmitCb,
  onCancel,
}: LoginRuleCriteriaFormProps) => {
  const { t } = useTranslation();
  const fields = getFieldsConfig({ t });
  const { data: loginRuleUserPoolFieldData } = useLoginRuleUserPoolFields();

  const aggDefaultValues = useMemo(() => {
    return fields.reduce((acc, field) => {
      const { name } = field.props || {};
      if (field.multiple) {
        acc[name] = defaultValues[name] || [];
      } else {
        acc[name] = defaultValues[name] || '';
      }

      return acc;
    }, {} as Form);
  }, [fields, defaultValues]);

  const { control, handleSubmit, formState } = useForm<Form>({
    defaultValues: aggDefaultValues,
  });

  const errors = formState.errors;

  const onSubmit = (data: Form) => {
    // clean up empty values from array fields
    const handledData = Object.keys(data).reduce((acc, key) => {
      // @ts-ignore
      acc[key] = Array.isArray(data[key])
        ? data[key].filter(Boolean)
        : data[key];

      return acc;
    }, {});

    onSubmitCb(handledData);
  };

  const multipleFieldsFormConfigs = fields
    .filter((field) => field.multiple)
    .reduce((map, fieldConfig) => {
      // eslint-disable-next-line
      const field = useFieldArray<Form>({
        control,
        name: fieldConfig.props.name as any,
      });

      if (field.fields.length === 0) {
        field.append(null);
      }

      map[fieldConfig.id] = field;

      return map;
    }, {} as MultipleFormField);

  return (
    <form>
      <div className="flex flex-col" data-testid="fields-factory">
        {fields.map((field) => {
          const { multiple: isMultiple, required: isRequired } = field || {};
          const { name: fieldName, label: fieldLabel } = field.props || {};
          const multipleFieldFormConfig = multipleFieldsFormConfigs[field.id];
          const isRequiredStyles = field.required
            ? 'border-l-4 border-red-500'
            : '';
          const fieldsGroup = isMultiple
            ? multipleFieldFormConfig.fields
            : [field];

          const ControlComponent = getComponentByType({
            type: field.type,
            data: loginRuleUserPoolFieldData,
          });
          if (!ControlComponent) {
            return null;
          }

          const id = field.id;
          return (
            <div className="flex flex-col" key={field.id}>
              <div className="flex flex-row pb-1 w-full">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  className="w-[80px] md:w-[150px] uppercase mt-3 pr-3"
                >
                  {fieldLabel}
                </Typography>
                <div className="flex-1">
                  {fieldsGroup.map((field: any, index) => {
                    const isLastGroupElement = fieldsGroup.length - 1 === index;
                    const isSingleGroupElement = fieldsGroup.length === 1;

                    return (
                      <div className={clsx('mb-2')} key={field.id}>
                        <div
                          className={`flex items-center justify-center ${isRequiredStyles}`}
                        >
                          <Controller
                            name={
                              isMultiple ? `${fieldName}[${index}]` : fieldName
                            }
                            control={control}
                            rules={{ required: isRequired }}
                            render={({ field }) => {
                              const value = field?.value;
                              const error = errors[
                                fieldName
                              ] as unknown as FieldError[];

                              return (
                                <ControlComponent
                                  {...(field as any)}
                                  value={value}
                                  defaultValue={value}
                                  error={
                                    !!error &&
                                    error.some(
                                      (item) => item?.ref?.name === field.name,
                                    )
                                  }
                                  key={id}
                                  fieldName={fieldName}
                                  isRequired={isRequired}
                                />
                              );
                            }}
                          />
                          {isMultiple && !isSingleGroupElement && (
                            <Close
                              className="scale-[1.5] ml-2 cursor-pointer"
                              stroke="#DE3730"
                              onClick={() =>
                                multipleFieldFormConfig.remove(index)
                              }
                            />
                          )}
                        </div>

                        {isMultiple && isLastGroupElement && (
                          <Button
                            variant="contained"
                            className="my-2 w-1/12"
                            onClick={() =>
                              multipleFieldFormConfig.append(undefined)
                            }
                          >
                            {t('login-rules.form.modal.or')}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end space-x-2 mt-4">
        <Button
          variant="contained"
          type="button"
          onClick={handleSubmit(onSubmit)}
        >
          {t('login-rules.form.modal.save')}
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          {t('login-rules.form.modal.cancel')}
        </Button>
      </div>
    </form>
  );
};
